/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import {
  Chemicals,
  Cotton,
  Dairy,
  Egg,
  Fish,
  Flower,
  Latex,
  Nuts,
  Peanuts,
  Pollen,
  Sesame,
  Shellfish,
  Wheat,
} from "assets/allergies";
import { Button, Card, Input, Stack, Text, FontAwesomeIcon } from "components";
import useCompanion from "hooks/useCompanion";
import { updatePreferences } from "lib/preferencesApi";
import { useEffect, useReducer, useState } from "react";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { usePrefNavigator } from "state/PrefNavigatorContext";
import { useUser } from "state/UserContext";
import { PageLayout } from "templates";
import { normalize } from "utils/capitalize";
import useGuestOptions from "./useGuestOptions";
import usePrefillPreferences from "./usePrefillPreferences";
import { useTranslation } from "react-i18next";
import { pushToDataLayer } from "utils/gtm";

export const safeAppend = (prev, value, separator) => {
  let output = value;
  if (prev && prev.length > 0) {
    output = prev;
    if (separator) {
      output = prev + separator;
    }
    output += value;
  }
  return output;
};

export const appendOtherOptions = (prev, value) => {
  if (prev && prev.length > 0) {
    return safeAppend(prev, value, ",other:");
  }
  return "other:" + value;
};

const stateInverter = (state, action) => {
  if (typeof action !== "boolean") {
    return !state;
  } else {
    return action;
  }
};
const ALLERGIES_DATA = [
  "egg",
  "dairy",
  "peanuts",
  "nuts",
  "fish",
  "shellfish",
  "wheat",
  "sesame",
  "other",
];
const PREFERENCES_DATA = ["vegetarian", "vegan", "pescatarian", "other"];

const BLOOD_TYPE_DATA = [
  "notSure",
  "O_plus",
  "O_minus",
  "A_plus",
  "A_minus",
  "B_plus",
  "B_minus",
  "AB_plus",
  "AB_minus",
];
const MATERIAL_ALLERGIES_DATA = [
  "latex",
  "chemicals",
  "flowers",
  "cotton",
  "pollen",
  "other",
];
const AllergiesIconsMapper = (name) =>
  ({
    egg: Egg,
    dairy: Dairy,
    peanuts: Peanuts,
    nuts: Nuts,
    fish: Fish,
    shellfish: Shellfish,
    wheat: Wheat,
    sesame: Sesame,
    other: null,
  })[name];

const generalAllergiesIconsMapper = (name) =>
  ({
    latex: Latex,
    chemicals: Chemicals,
    flowers: Flower,
    cotton: Cotton,
    pollen: Pollen,
    other: null,
  })[name];

const Preferences = () => {
  const { t } = useTranslation();
  const { userId, eventId } = useParams();

  const { setLoading, updateProfile, isGuest, userData } = useUser();
  const [companion, setCompanion] = useCompanion(userId);
  const [isEditable, setEditable] = useReducer(stateInverter, false);
  const [inputsData, setInputsData] = useState({
    notes: "",
    customPref: "",
    foodAllergy: "",
    chemicalAllergy: "",
    physicalAllergy: "",
    medicalCondition: "",
    specialAssistance: "",
  });

  const { enabledSteps } = usePrefNavigator();

  const [allergiesData, setAllergiesData] = useGuestOptions(ALLERGIES_DATA);
  const [preferences, setPreferences] = useGuestOptions(PREFERENCES_DATA);

  const [materialAllergiesData, setMaterialAllergiesData] = useGuestOptions(
    MATERIAL_ALLERGIES_DATA,
  );

  const [bloodTypeData, setBloodTypeData, resetAllBloodTypes] =
    useGuestOptions(BLOOD_TYPE_DATA);

  const set = (setter, key) => {
    setter((prev) => {
      if (prev[key]?.state === "success") {
        return { ...prev, [key]: { ...prev[key], state: "initial" } };
      }
      return { ...prev, [key]: { ...prev[key], state: "success" } };
    });
  };

  const handleOnChange = (key, source) => {
    if (!isEditable) {
      setEditable(true);
    }
    if (source === "prefs") {
      set(setPreferences, key);
      // setPreferences((prev) => {
      //   if (preferences[text]?.state === "success") {
      //     return { ...prev, [text]: { value: text, state: "initial" } };
      //   }
      //   return { ...prev, [text]: { value: text, state: "success" } };
      // });
    }
    if (source === "allergies") {
      set(setAllergiesData, key);
      // setAllergiesData((prev) => {
      //   if (allergiesData[key]?.state === "success") {
      //     return { ...prev, [key]: { value: key, state: "initial" } };
      //   }
      //   return { ...prev, [key]: { value: key, state: "success" } };
      // });
    }
    if (source === "materialAllergies") {
      set(setMaterialAllergiesData, key);
      // setMaterialAllergiesData((prev) => {
      //   if (materialAllergiesData[key]?.state === "success") {
      //     return { ...prev, [key]: { value: key, state: "initial" } };
      //   }
      //   return { ...prev, [key]: { value: key, state: "success" } };
      // });
    }
    if (source === "bloodType") {
      resetAllBloodTypes();
      setBloodTypeData((prev) => ({
        ...prev,
        [key]: { ...prev[key], state: "success" },
      }));
    }
  };

  const handleOnInput = (e) => {
    const { name, value } = e.target;

    setInputsData((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };
  const allergies = companion?.allergies;
  const foodPreferences = companion?.food_preferences;
  const bloodType = companion?.blood_type;
  const physicalAllergies = companion?.general_allergies;

  const handleCustomNote = (field, value) => {
    setInputsData((prev) => {
      return { ...prev, [field]: value };
    });
  };
  const onFoodAllergy = (value) => handleCustomNote("foodAllergy", value);
  const onCustomPref = (value) => handleCustomNote("customPref", value);
  const onCustomPhysicalAllergy = (value) =>
    handleCustomNote("physicalAllergy", value);
  usePrefillPreferences(allergies, {
    setter: setAllergiesData,
    setCustomNote: onFoodAllergy,
  });
  usePrefillPreferences(foodPreferences, {
    setter: setPreferences,
    setCustomNote: onCustomPref,
  });
  usePrefillPreferences(bloodType, {
    setter: setBloodTypeData,
  });
  usePrefillPreferences(physicalAllergies, {
    setter: setMaterialAllergiesData,
    setCustomNote: onCustomPhysicalAllergy,
  });

  useEffect(() => {
    if (companion?.other_allergies) {
      setMaterialAllergiesData((prev) => {
        return {
          ...prev,
          other: {
            ...prev.other,
            value: companion?.other_allergies,
            state: "success",
          },
        };
      });
      setInputsData((prev) => {
        return { ...prev, physicalAllergy: companion?.other_allergies };
      });
    }
    if (companion?.chemical_allergies) {
      setMaterialAllergiesData((prev) => {
        return {
          ...prev,
          chemicals: {
            ...prev.chemicals,
            value: companion?.chemical_allergies,
            state: "success",
          },
        };
      });
      setInputsData((prev) => {
        return { ...prev, chemicalAllergy: companion?.chemical_allergies };
      });
    }
    if (companion?.special_medical_assistance) {
      setInputsData((prev) => {
        return {
          ...prev,
          specialAssistance: companion?.special_medical_assistance,
        };
      });
    }
    if (companion?.other_notes) {
      setInputsData((prev) => {
        return { ...prev, notes: companion?.other_notes };
      });
    }
  }, [companion]);
  const handleOnSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const body = {
      food_preferences: "",
      allergies: "",
      blood_type: "",
      general_allergies: "",
      other_notes: "",
      chemical_allergies: "",
      special_medical_assistance: "",
    };
    const processOption = (object, fieldName, inputName) => {
      if (object["state"] === "success") {
        const value = object["value"];
        if (!value) return;
        const prev = body[fieldName];
        if (!inputName.includes("other") && !inputName.includes("chemicals")) {
          const newValue = safeAppend(prev, value, ",");
          body[fieldName] = newValue;
        }
        // if (!body[fieldName] || body[fieldName].length === 0) {
        //   body[fieldName] = value;
        // } else {
        //   body[fieldName] = body[fieldName] + "," + value;
        // }
      }
    };
    if (preferences) {
      Object.entries(preferences).forEach(([inputName, v]) => {
        processOption(v, "food_preferences", inputName);
      });
    }
    if (allergiesData) {
      Object.entries(allergiesData).forEach(([inputName, v]) => {
        processOption(v, "allergies", inputName);
      });
    }
    if (materialAllergiesData) {
      Object.entries(materialAllergiesData).forEach(([inputName, v]) => {
        processOption(v, "general_allergies", inputName);
      });
    }
    if (bloodTypeData) {
      Object.entries(bloodTypeData).forEach(([inputName, v]) => {
        processOption(v, "blood_type", inputName);
      });
    }

    if (
      inputsData.customPref.length > 0 &&
      preferences.other.state === "success"
    ) {
      body["food_preferences"] = appendOtherOptions(
        body["food_preferences"],
        inputsData.customPref,
      );
    }

    if (
      inputsData.foodAllergy.length > 0 &&
      allergiesData.other.state === "success"
    ) {
      body["allergies"] = appendOtherOptions(
        body["allergies"],
        inputsData.foodAllergy,
      );
    }

    if (
      materialAllergiesData.pollen.state !== "success" &&
      materialAllergiesData.latex.state !== "success" &&
      materialAllergiesData.cotton.state !== "success" &&
      materialAllergiesData.flowers.state !== "success"
    ) {
      body["general_allergies"] = "";
    }

    if (
      inputsData.physicalAllergy.length > 0 &&
      materialAllergiesData.other.state === "success"
    ) {
      body["other_allergies"] = inputsData.physicalAllergy;
    } else {
      body["other_allergies"] = "";
    }
    if (
      inputsData.chemicalAllergy.length > 0 &&
      materialAllergiesData?.chemicals?.state === "success"
    ) {
      body["chemical_allergies"] = inputsData.chemicalAllergy;
    } else {
      body["chemical_allergies"] = "";
    }

    if (
      inputsData.specialAssistance !== companion?.special_medical_assistance
    ) {
      body["special_medical_assistance"] = inputsData.specialAssistance;
    } else {
      body["special_medical_assistance"] =
        companion?.special_medical_assistance;
    }

    if (inputsData.notes !== companion?.other_notes) {
      body["other_notes"] = inputsData.notes;
    } else {
      body["other_notes"] = companion?.other_notes;
    }

    let id;
    const isCompanion = companion?.id !== userData.id;
    if (isCompanion) {
      id = companion?.id;
    } else {
      id = companion?.hash_id;
    }
    body.isCompanion = isCompanion;
    const { ok } = await updatePreferences(id, body, eventId);
    setLoading(false);
    if (ok === true) {
      toast.success("Updated successfully");
      // @@NEED REVIEW
      const data = {
        ...companion,
        ...body,
      };
      setCompanion(data);
      updateProfile(id, data);
    } else {
      toast.error("Something went wrong...");
    }
  };

  return (
    <PageLayout
      hasGoBack={!isGuest}
      title={t("specialConcerns.title")}
      headingButtons={
        <div>
          {isEditable ? (
            <div className="flex gap-x-2">
              <Button
                className="w-32 hidden sm:block"
                variant="secondary"
                onClick={(e) => {
                  pushToDataLayer({
                    event: "cancel_special_concerns",
                    screen_name: "special concerns",
                    journey_step: "special concerns",
                  });
                  setEditable(false);
                }}
                size="md"
              >
                <span>{t("specialConcerns.cancelButtonLabel")}</span>
              </Button>

              <Button
                variant="primary"
                className="w-32"
                // isLoading={isLoading}
                onClick={(e) => {
                  pushToDataLayer({
                    event: "save_special_concerns",
                    screen_name: "special concerns",
                    journey_step: "special concerns",
                  });
                  setEditable(false);
                  handleOnSubmit(e);
                }}
                size="md"
              >
                {t("specialConcerns.saveButtonLabel")}
              </Button>
            </div>
          ) : (
            <div className="h-14 w-28">
              <Button
                variant="tertiary"
                onClick={() => {
                  pushToDataLayer({
                    event: "edit_special_concerns_click",
                    screen_name: "special concerns",
                    journey_step: "special concerns",
                  });
                  setEditable(true);
                }}
              >
                <div className="flex items-center justify-center gap-x-4">
                  <FontAwesomeIcon name="fa-pen" />
                  <span>{t("specialConcerns.editButtonLabel")}</span>
                </div>
              </Button>
            </div>
          )}
        </div>
      }
    >
      <div className="grid md:grid-cols-2 gap-x-32 mt-5 gap-y-10">
        {enabledSteps.some((step) => step.step === "preferences") && (
          <Stack className="gap-y-5">
            <Text className="-text--component-colors-field-default-label-text">
              {t("preferences.title")}
            </Text>
            <Stack
              component="form"
              className="justify-between sm:justify-center"
            >
              <Stack className="space-y-4">
                {PREFERENCES_DATA.map((key) => {
                  const value = preferences[key];
                  return (
                    <Card
                      title={t(`preferences.${key}`, {
                        defaultValue: normalize(key),
                      })}
                      key={key}
                      state={value?.state}
                      variant="pref"
                      onClick={() => handleOnChange(key, "prefs")}
                      isDisabled={!isEditable && value?.state !== "success"}
                    />
                  );
                })}
                {preferences.other.state === "success" && (
                  <div>
                    {isEditable ? (
                      <Input
                        placeholder={t("guestInfoPage.otherPlaceholder")}
                        variant="custom"
                        name="customPref"
                        type="text"
                        id="customPref"
                        label={t("preferences.specialConcernsOther")}
                        value={inputsData.customPref}
                        onChange={handleOnInput}
                        spellCheck={false}
                        className="mt-2"
                      />
                    ) : (
                      <>
                        <Text
                          htmlFor="customPref"
                          className="-text--component-colors-field-default-label-text"
                        >
                          {t("preferences.specialConcernsOther")}
                        </Text>
                        <Text className="-text--theme-colors-text-body">
                          {!inputsData.customPref.length
                            ? t("preferences.specialConcernsNoPreference")
                            : inputsData.customPref}
                        </Text>
                      </>
                    )}
                  </div>
                )}
              </Stack>
            </Stack>
          </Stack>
        )}
        {enabledSteps.some((step) => step.step === "allergies") && (
          <Stack component="form" className="gap-y-5">
            <Text className="-text--component-colors-field-default-label-text">
              {t("allergies.title")}
            </Text>

            <div className="grid grid-cols-3 gap-2">
              {ALLERGIES_DATA.map((key) => {
                const value = allergiesData[key];
                return (
                  <Card
                    title={t(`allergies.${key}`, {
                      defaultValue: normalize(key),
                    })}
                    key={key}
                    variant="allergy"
                    media={AllergiesIconsMapper(key)}
                    state={value?.state}
                    onClick={() => handleOnChange(key, "allergies")}
                    isDisabled={!isEditable && value?.state !== "success"}
                  />
                );
              })}
            </div>

            {allergiesData.other.state === "success" && (
              <div>
                {isEditable ? (
                  <Input
                    placeholder={t("guestInfoPage.otherPlaceholder")}
                    variant="custom"
                    name="foodAllergy"
                    id="foodAllergy"
                    type="text"
                    value={inputsData.foodAllergy}
                    onChange={handleOnInput}
                    spellCheck={false}
                    label={t("allergies.specialConcernsOther")}
                  />
                ) : (
                  <>
                    <Text className="-text--component-colors-field-default-label-text">
                      {t("allergies.specialConcernsOther")}
                    </Text>
                    <Text className="-text--theme-colors-text-body">
                      {inputsData.foodAllergy === ""
                        ? t("allergies.specialConcernsNoPreference")
                        : inputsData.foodAllergy}
                    </Text>
                  </>
                )}
              </div>
            )}
          </Stack>
        )}
        {enabledSteps.some((step) => step.step === "generalAllergies") && (
          <Stack component="form" className="gap-y-5">
            <Text className="-text--component-colors-field-default-label-text">
              {t("generalAllergies.specialConcernsTitle")}
            </Text>
            <div className="grid grid-cols-3 gap-1">
              {MATERIAL_ALLERGIES_DATA.map((key) => {
                const value = materialAllergiesData[key];
                return (
                  <Card
                    title={t(`generalAllergies.${key}`, {
                      defaultValue: normalize(key),
                    })}
                    key={key}
                    variant="allergy"
                    media={generalAllergiesIconsMapper(key)}
                    state={value?.state}
                    onClick={() => handleOnChange(key, "materialAllergies")}
                    isDisabled={!isEditable && value?.state !== "success"}
                  />
                );
              })}
            </div>
            {materialAllergiesData.chemicals?.state === "success" && (
              <div>
                {isEditable ? (
                  <Input
                    variant="custom"
                    name="chemicalAllergy"
                    id="chemicalAllergy"
                    type="text"
                    value={inputsData.chemicalAllergy}
                    onChange={handleOnInput}
                    disabled={!isEditable}
                    label={t("generalAllergies.specialConcernsOther")}
                  />
                ) : (
                  <>
                    <Text className="-text--component-colors-field-default-label-text">
                      {t("generalAllergies.specialConcernsOther")}
                    </Text>
                    <Text className="-text--theme-colors-text-body">
                      {inputsData.chemicalAllergy === ""
                        ? t("generalAllergies.specialConcernsNoPreference")
                        : inputsData.chemicalAllergy}
                    </Text>
                  </>
                )}
              </div>
            )}
            {materialAllergiesData.other.state === "success" && (
              <div>
                {isEditable ? (
                  <Input
                    variant="custom"
                    name="physicalAllergy"
                    id="physicalAllergy"
                    type="text"
                    value={inputsData.physicalAllergy}
                    onChange={handleOnInput}
                    disabled={!isEditable}
                    label={t("generalAllergies.specialConcernsOtherMaterial")}
                  />
                ) : (
                  <>
                    <Text className="-text--component-colors-field-default-label-text">
                      {t("generalAllergies.specialConcernsOtherMaterial")}
                    </Text>
                    <Text className="-text--theme-colors-text-body">
                      {inputsData.physicalAllergy
                        ? inputsData.physicalAllergy
                        : t("generalAllergies.specialConcernsNoMaterial")}
                    </Text>
                  </>
                )}
              </div>
            )}
          </Stack>
        )}
        <Stack className="gap-y-10">
          {enabledSteps.some((step) => step.step === "notes") && (
            <Stack component="form" className="gap-y-5">
              {/* <Text className="-text--component-colors-field-default-label-text">
                {t('medicalConditions.bloodTypeLabel')}
              </Text> */}
              {/* <div className="grid grid-cols-3 gap-1">
                {Object.entries(bloodTypeData).map(([key, value]) => (
                  <Card
                    title={key === "notSure" ? "Not Sure" : ""}
                    key={key}
                    variant="allergy"
                    media={MedicalConditionsIcons(key)}
                    state={value?.state}
                    onClick={() => handleOnChange(key, "bloodType")}
                    isDisabled={!isEditable && value?.state !== "success"}
                  />
                ))}
              </div> */}

              <div>
                {isEditable ? (
                  <Input
                    variant="custom"
                    name="specialAssistance"
                    id="specialAssistance"
                    type="text"
                    value={inputsData.specialAssistance}
                    onChange={handleOnInput}
                    disabled={!isEditable}
                    label={t("medicalConditions.specialAssistanceLabel")}
                  />
                ) : (
                  <>
                    <Text className="-text--component-colors-field-default-label-text">
                      {t("medicalConditions.specialAssistanceLabel")}
                    </Text>
                    <Text className="-text--theme-colors-text-body">
                      {inputsData.specialAssistance === ""
                        ? t(
                            "medicalConditions.specialConcernsNoSpecialAssistance",
                          )
                        : inputsData.specialAssistance}
                    </Text>
                  </>
                )}
              </div>
            </Stack>
          )}
          {enabledSteps.some((step) => step.step === "notes") && (
            <Stack component="form" className="gap-y-5">
              <div>
                {isEditable && (
                  <Input
                    placeholder={t(
                      "medicalConditions.specialConcernsOtherNotesPlaceholder",
                    )}
                    variant="custom"
                    id="notes"
                    name="notes"
                    type="text"
                    value={inputsData.notes}
                    onChange={handleOnInput}
                    spellCheck={false}
                    maxLength={256}
                    disabled={!isEditable}
                    label={t("medicalConditions.specialConcernsOtherNotes")}
                  />
                )}
                {!isEditable && (
                  <>
                    <Text className="-text--component-colors-field-default-label-text">
                      {t("medicalConditions.specialConcernsOtherNotes")}
                    </Text>
                    <Text className="-text--theme-colors-text-body">
                      {inputsData.notes === ""
                        ? t("medicalConditions.specialConcernsNoPreference")
                        : inputsData.notes}
                    </Text>
                  </>
                )}
              </div>
            </Stack>
          )}
        </Stack>
      </div>
    </PageLayout>
  );
};

export default Preferences;
